import { reportProviderWorkingLocation } from "../provider-workspace/provider-working-location";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type PaymentMethod,
  type ProviderSession,
  type ProviderVenue,
  type ReceiptPaperWidth,
  type ReceiptRecord,
  type SessionReceiptPreviewRecord,
  providerApi,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import {
  buildReceiptPrintDocument,
  closeReceiptPrintWindow,
  openReceiptPrintWindow,
  printReceiptDocument,
} from "../../lib/receipt-print";
import { ProviderReportsCard } from "../provider-workspace/ProviderReportsCard";
import { NoShiftCashNotice } from "../provider-shift/TodayCards";

function formatMoney(amount: number, currency: string, locale: string) {
  return new Intl.NumberFormat(locale, {
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: "currency",
  }).format(amount);
}

function formatDateTime(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function sessionLabel(session: ProviderSession) {
  return `${session.customer_name} · ${session.unit_name} · ${session.status}`;
}

function amountToCents(value: number | string) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function centsToAmount(cents: number) {
  return cents / 100;
}

function appliedPaymentCents(receivedCents: number, balanceDueCents: number, method: PaymentMethod | "") {
  if (!method) {
    return 0;
  }
  return method === "cash" ? Math.min(receivedCents, balanceDueCents) : receivedCents;
}

function changeDueCentsFromReceived(receivedCents: number, balanceDueCents: number, method: PaymentMethod | "") {
  return method === "cash" ? Math.max(0, receivedCents - balanceDueCents) : 0;
}

function paymentChangeAmount(payment: { metadata_json: Record<string, unknown> | null }) {
  const value = payment.metadata_json?.change_due_amount;
  return typeof value === "number" ? value : null;
}

function paymentTenderedAmount(payment: { metadata_json: Record<string, unknown> | null }) {
  const value = payment.metadata_json?.cash_received_amount;
  return typeof value === "number" ? value : null;
}

const receiptPaymentMethods: PaymentMethod[] = ["cash", "card", "transfer", "other"];

const receiptStatusLabelKey: Record<string, string> = {
  issued: "Issued",
  voided: "Voided",
  refunded: "Refunded",
};

/** "cash" → "Cash", translated. */
function methodLabel(method: string, t: (key: string) => string) {
  return t(method.charAt(0).toUpperCase() + method.slice(1));
}

export function ProviderReceiptsCard({
  checkoutSessionId,
  isOnline,
  onReceiptChange,
  recentSessions,
  selectedVenue,
  sessions,
}: {
  checkoutSessionId?: string | null;
  isOnline: boolean;
  onReceiptChange?: () => Promise<void> | void;
  recentSessions: ProviderSession[];
  selectedVenue: ProviderVenue | null;
  sessions: ProviderSession[];
}) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-US";
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [preview, setPreview] = useState<SessionReceiptPreviewRecord | null>(null);
  const [receipts, setReceipts] = useState<ReceiptRecord[]>([]);
  const [receiptSearch, setReceiptSearch] = useState("");
  const [receiptStatusFilter, setReceiptStatusFilter] = useState("");
  const [selectedReceiptId, setSelectedReceiptId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [paidAmount, setPaidAmount] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundMethod, setRefundMethod] = useState<PaymentMethod>("cash");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [activeTab, setActiveTab] = useState<"register" | "history">("register");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [charging, setCharging] = useState(false);
  const [voidingReceipt, setVoidingReceipt] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [receiptPaperWidth, setReceiptPaperWidth] = useState<ReceiptPaperWidth>("80mm");
  const billRef = useRef<HTMLElement | null>(null);
  // On narrow screens the bill sits below the list; bring it into view after a tap.
  const revealBill = () => {
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 999px)").matches) {
      requestAnimationFrame(() => billRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };



  const sessionOptions = useMemo(() => {
    const byId = new Map<string, ProviderSession>();
    [...sessions, ...recentSessions]
      .filter((session) => !selectedVenue || session.venue_id === selectedVenue.id)
      .forEach((session) => byId.set(session.id, session));
    return Array.from(byId.values()).sort((left, right) => Date.parse(right.started_at) - Date.parse(left.started_at));
  }, [recentSessions, selectedVenue, sessions]);

  useEffect(() => {
    setSelectedSessionId((current) => {
      if (checkoutSessionId && sessionOptions.some((session) => session.id === checkoutSessionId)) {
        return checkoutSessionId;
      }
      if (current && sessionOptions.some((session) => session.id === current)) {
        return current;
      }
      return sessionOptions[0]?.id ?? "";
    });
  }, [checkoutSessionId, sessionOptions]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedVenue) {
      setReceipts([]);
      return;
    }

    setError(null);
    void providerApi.listReceipts({
      search: receiptSearch.trim() || undefined,
      statusFilter: receiptStatusFilter || undefined,
      venueId: selectedVenue.id,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    })
      .then((response) => {
        if (!cancelled) {
          setReceipts(response.receipts);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("Could not load receipts."));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [receiptSearch, receiptStatusFilter, dateFrom, dateTo, selectedVenue, t]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedVenue) {
      setReceiptPaperWidth("80mm");
      return;
    }

    void providerApi.getVenueSettings(selectedVenue.id)
      .then((settings) => {
        if (!cancelled) {
          setReceiptPaperWidth(settings.receipt_paper_width === "58mm" ? "58mm" : "80mm");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReceiptPaperWidth("80mm");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedVenue]);



  useEffect(() => {
    setSelectedReceiptId((current) => {
      if (checkoutSessionId && activeTab === "register") {
        const linkedReceipt = receipts.find((receipt) => receipt.source_type === "session" && receipt.source_id === checkoutSessionId);
        return linkedReceipt?.id ?? "";
      }
      if (current && receipts.some((receipt) => receipt.id === current)) {
        return current;
      }
      return receipts[0]?.id ?? "";
    });
  }, [activeTab, checkoutSessionId, receipts]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedSessionId) {
      setPreview(null);
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);
    void providerApi.getSessionReceiptPreview(selectedSessionId)
      .then((nextPreview) => {
        if (cancelled) {
          return;
        }
        setPreview(nextPreview);
        const defaultMethod = nextPreview.allowed_payment_methods[0] ?? "";
        const defaultPaidAmount = nextPreview.balance_due > 0 ? nextPreview.balance_due.toFixed(2) : "0.00";
        setPaymentMethod(defaultMethod);
        setPaidAmount(defaultPaidAmount);
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setError(t("Could not build receipt preview."));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSessionId, t]);

  const selectedSession = sessionOptions.find((session) => session.id === selectedSessionId) ?? null;
  useEffect(() => {
    if (!selectedVenue || (checkoutSessionId && !selectedSession)) return;
    reportProviderWorkingLocation({ destination: "register", context: selectedSession
      ? { kind: "session", venue_id: selectedVenue.id, record_id: selectedSession.id }
      : { kind: "venue", venue_id: selectedVenue.id } });
  }, [selectedVenue?.id, selectedSession?.id, checkoutSessionId]);

  const sessionReceipt = receipts.find((receipt) => receipt.source_type === "session" && receipt.source_id === selectedSessionId) ?? null;
  const selectedReceiptById = receipts.find((receipt) => receipt.id === selectedReceiptId) ?? null;
  const isCheckoutTargetActive = Boolean(checkoutSessionId && selectedSessionId === checkoutSessionId);
  const selectedReceipt = sessionReceipt ?? selectedReceiptById;
  const displayedCurrency = sessionReceipt?.currency ?? preview?.currency ?? "EGP";
  const displayedLines = sessionReceipt?.lines ?? preview?.lines ?? [];
  const displayedSubtotal = sessionReceipt?.subtotal_amount ?? preview?.subtotal_amount ?? 0;
  const displayedServiceFee = sessionReceipt?.service_fee_amount ?? preview?.service_fee_amount ?? 0;
  const displayedTax = sessionReceipt?.tax_amount ?? preview?.tax_amount ?? 0;
  const displayedTotal = sessionReceipt?.total_amount ?? preview?.total_amount ?? 0;
  const displayedPaid = sessionReceipt?.paid_amount ?? preview?.paid_amount ?? 0;
  const displayedBalance = sessionReceipt?.balance_due ?? preview?.balance_due ?? 0;
  const activeCurrency = selectedReceipt?.currency ?? displayedCurrency;
  const activeLines = selectedReceipt?.lines ?? displayedLines;
  const activeSubtotal = selectedReceipt?.subtotal_amount ?? displayedSubtotal;
  const activeServiceFee = selectedReceipt?.service_fee_amount ?? displayedServiceFee;
  const activeTax = selectedReceipt?.tax_amount ?? displayedTax;
  const activeTotal = selectedReceipt?.total_amount ?? displayedTotal;
  const activePaid = selectedReceipt?.paid_amount ?? displayedPaid;
  const activeBalance = selectedReceipt?.balance_due ?? displayedBalance;

  useEffect(() => {
    if (checkoutSessionId) {
      setActiveTab("register");
    }
  }, [checkoutSessionId]);

  useEffect(() => {
    if (preview) {
      const defaultMethod = preview.allowed_payment_methods[0] ?? "";
      setPaymentMethod(defaultMethod);
      setPaidAmount(preview.balance_due > 0 ? preview.balance_due.toFixed(2) : "0.00");
    } else if (selectedReceipt && selectedReceipt.balance_due > 0) {
      setPaymentMethod("cash");
      setPaidAmount(selectedReceipt.balance_due.toFixed(2));
    } else {
      setPaymentMethod("");
      setPaidAmount("");
    }
    setVoidReason("");
  }, [preview, selectedReceipt]);

  useEffect(() => {
    if (isCheckoutTargetActive && !sessionReceipt && selectedReceiptId) {
      setSelectedReceiptId("");
    }
  }, [isCheckoutTargetActive, selectedReceiptId, sessionReceipt]);

  useEffect(() => {
    const linkedReceipt = receipts.find((receipt) => receipt.id === selectedReceiptId);
    if (!linkedReceipt || linkedReceipt.source_type !== "session" || !linkedReceipt.source_id) {
      return;
    }
    if (!sessionOptions.some((session) => session.id === linkedReceipt.source_id)) {
      return;
    }
    setSelectedSessionId((current) => current === linkedReceipt.source_id ? current : linkedReceipt.source_id ?? current);
  }, [receipts, selectedReceiptId, sessionOptions]);

  const selectSession = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
    const linkedReceipt = receipts.find((receipt) => receipt.source_type === "session" && receipt.source_id === sessionId);
    setSelectedReceiptId(linkedReceipt?.id ?? "");
  }, [receipts]);

  const selectReceipt = useCallback((receipt: ReceiptRecord) => {
    setSelectedReceiptId(receipt.id);
    if (receipt.source_type === "session" && receipt.source_id && sessionOptions.some((session) => session.id === receipt.source_id)) {
      setSelectedSessionId(receipt.source_id);
    } else {
      setSelectedSessionId("");
    }
  }, [sessionOptions]);

  const refreshReceiptLane = async (receipt: ReceiptRecord, { syncWorkspace = true }: { syncWorkspace?: boolean } = {}) => {
    const freshReceipt = await providerApi.getReceipt(receipt.id).catch(() => receipt);

    setReceipts((current) => [freshReceipt, ...current.filter((item) => item.id !== freshReceipt.id)]);
    setSelectedReceiptId(freshReceipt.id);
    if (freshReceipt.source_type === "session" && freshReceipt.source_id && sessionOptions.some((session) => session.id === freshReceipt.source_id)) {
      setSelectedSessionId(freshReceipt.source_id);
    }

    const previewSessionId = freshReceipt.source_type === "session" && freshReceipt.source_id ? freshReceipt.source_id : selectedSessionId;
    if (previewSessionId) {
      const nextPreview = await providerApi.getSessionReceiptPreview(previewSessionId).catch(() => null);
      if (nextPreview) {
        setPreview(nextPreview);
      }
    }

    if (selectedVenue) {
      const receiptList = await providerApi.listReceipts({
        search: receiptSearch.trim() || undefined,
        statusFilter: receiptStatusFilter || undefined,
        venueId: selectedVenue.id,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }).catch(() => null);
      if (receiptList) {
        setReceipts(() => {
          const nextReceipts = receiptList.receipts;
          if (nextReceipts.some((item) => item.id === freshReceipt.id)) {
            return nextReceipts;
          }
          return [freshReceipt, ...nextReceipts];
        });
      }
    }

    if (syncWorkspace) {
      await onReceiptChange?.();
    }

    return freshReceipt;
  };

  const sessionReceiptsById = useMemo(() => {
    const map = new Map<string, ReceiptRecord>();
    receipts.forEach((receipt) => {
      if (receipt.source_type === "session" && receipt.source_id) {
        map.set(receipt.source_id, receipt);
      }
    });
    return map;
  }, [receipts]);

  const registerSessions = useMemo(() => {
    return [...sessionOptions].sort((left, right) => {
      const leftReceipt = sessionReceiptsById.get(left.id);
      const rightReceipt = sessionReceiptsById.get(right.id);
      const leftNeedsAttention = !leftReceipt || (leftReceipt.status === "issued" && leftReceipt.balance_due > 0);
      const rightNeedsAttention = !rightReceipt || (rightReceipt.status === "issued" && rightReceipt.balance_due > 0);
      if (leftNeedsAttention !== rightNeedsAttention) {
        return leftNeedsAttention ? -1 : 1;
      }
      return Date.parse(right.started_at) - Date.parse(left.started_at);
    });
  }, [sessionOptions, sessionReceiptsById]);

  const chargeBalanceCents = amountToCents(sessionReceipt?.balance_due ?? preview?.balance_due ?? selectedReceipt?.balance_due ?? 0);
  const chargeCurrency = sessionReceipt?.currency ?? preview?.currency ?? selectedReceipt?.currency ?? "EGP";
  const chargeReceivedCents = amountToCents(paidAmount);
  const chargeAppliedCents = appliedPaymentCents(chargeReceivedCents, chargeBalanceCents, paymentMethod);
  const chargeChangeDueCents = changeDueCentsFromReceived(chargeReceivedCents, chargeBalanceCents, paymentMethod);
  const chargeAppliedAmount = centsToAmount(chargeAppliedCents);
  const chargeChangeDue = centsToAmount(chargeChangeDueCents);
  const canCharge = Boolean((preview || sessionReceipt || selectedReceipt) && !loading && (sessionReceipt?.status === "issued" || !sessionReceipt) && chargeBalanceCents > 0);
  const chargeInvalid = !canCharge || chargeReceivedCents <= 0 || chargeAppliedCents <= 0 || chargeAppliedCents > chargeBalanceCents || (paymentMethod !== "cash" && chargeReceivedCents > chargeBalanceCents);
  const selectedReceiptPaidCents = selectedReceipt ? amountToCents(selectedReceipt.paid_amount) : 0;
  const selectedReceiptHasPayments = Boolean(selectedReceipt && (selectedReceiptPaidCents > 0 || selectedReceipt.payments.length > 0));
  const canVoidSelectedReceipt = Boolean(selectedReceipt && selectedReceipt.status === "issued" && !selectedReceiptHasPayments);
  const voidReceiptInvalid = !canVoidSelectedReceipt || voidReason.trim().length < 3;

  const recordCharge = async () => {
    if (!preview && !sessionReceipt && !selectedReceipt) {
      return;
    }

    setCharging(true);
    setError(null);
    setNotice(null);

    try {
      if (chargeInvalid) {
        throw new Error(t("Enter a received amount within the receipt balance."));
      }
      if (!sessionReceipt && preview) {
        const receipt = await providerApi.issueSessionReceipt(selectedSessionId, {
          cash_received_amount: paymentMethod === "cash" && chargeAppliedCents > 0 ? centsToAmount(chargeReceivedCents) : null,
          mark_session_paid: true,
          paid_amount: chargeAppliedAmount,
          payment_method: paymentMethod || null,
        });
        await refreshReceiptLane(receipt);
        setNotice(t("Receipt {number} issued and paid.", { number: receipt.receipt_number }));
      } else if (sessionReceipt) {
        const updatedReceipt = await providerApi.recordReceiptPayment(sessionReceipt.id, {
          amount: chargeAppliedAmount,
          cash_received_amount: paymentMethod === "cash" ? centsToAmount(chargeReceivedCents) : null,
          method: paymentMethod as PaymentMethod,
        });
        await refreshReceiptLane(updatedReceipt);
        setNotice(t("Payment recorded for {number}.", { number: updatedReceipt.receipt_number }));
      } else if (selectedReceipt) {
        const updatedReceipt = await providerApi.recordReceiptPayment(selectedReceipt.id, {
          amount: chargeAppliedAmount,
          cash_received_amount: paymentMethod === "cash" ? centsToAmount(chargeReceivedCents) : null,
          method: paymentMethod as PaymentMethod,
        });
        await refreshReceiptLane(updatedReceipt);
        setNotice(t("Payment recorded for {number}.", { number: updatedReceipt.receipt_number }));
      }
    } catch (chargeError) {
      setError(chargeError instanceof Error ? chargeError.message : t("Could not record payment."));
    } finally {
      setCharging(false);
    }
  };

  const issueUnpaidReceipt = async () => {
    if (!selectedSessionId || !preview || sessionReceipt) {
      return;
    }

    setCharging(true);
    setError(null);
    setNotice(null);

    try {
      const receipt = await providerApi.issueSessionReceipt(selectedSessionId, {
        cash_received_amount: null,
        mark_session_paid: false,
        paid_amount: null,
        payment_method: null,
      });
      await refreshReceiptLane(receipt);
      setNotice(t("Receipt {number} issued.", { number: receipt.receipt_number }));
    } catch (issueError) {
      setError(issueError instanceof Error ? issueError.message : t("Could not issue receipt."));
    } finally {
      setCharging(false);
    }
  };

  const canIssuePaidReceipt = Boolean(preview && !sessionReceipt && chargeBalanceCents === 0 && amountToCents(preview.paid_amount) > 0);

  const issuePaidReceipt = async () => {
    if (!selectedSessionId || !preview || sessionReceipt) {
      return;
    }

    setCharging(true);
    setError(null);
    setNotice(null);

    try {
      const receipt = await providerApi.issueSessionReceipt(selectedSessionId, {
        cash_received_amount: null,
        mark_session_paid: true,
        paid_amount: 0,
        payment_method: null,
      });
      await refreshReceiptLane(receipt);
      setNotice(t("Receipt {number} issued and paid.", { number: receipt.receipt_number }));
    } catch (issueError) {
      setError(issueError instanceof Error ? issueError.message : t("Could not issue receipt."));
    } finally {
      setCharging(false);
    }
  };

  const printSelectedReceipt = async () => {
    if (!selectedReceipt) {
      return;
    }
    const venue = selectedVenue;
    if (!venue) {
      return;
    }

    setError(null);
    setNotice(null);
    const popup = openReceiptPrintWindow();
    if (!popup) {
      setError(t("Could not open the receipt print window. Allow popups and try again."));
      return;
    }

    try {
      const documentHtml = buildReceiptPrintDocument({
        direction: resolvedLanguage === "ar" ? "rtl" : "ltr",
        language: resolvedLanguage,
        paperWidth: receiptPaperWidth,
        title: t("Receipt"),
        venueName: venue.name,
        receiptNumber: selectedReceipt.receipt_number,
        issuedAt: formatDateTime(selectedReceipt.issued_at, locale),
        customerName: selectedReceipt.customer_name ?? t("Walk-in"),
        lines: selectedReceipt.lines.map((line) => ({
          description: line.description,
          quantity: String(line.quantity),
          total: formatMoney(line.total, selectedReceipt.currency, locale),
        })),
        subtotal: formatMoney(selectedReceipt.subtotal_amount, selectedReceipt.currency, locale),
        serviceFee: formatMoney(selectedReceipt.service_fee_amount, selectedReceipt.currency, locale),
        tax: formatMoney(selectedReceipt.tax_amount, selectedReceipt.currency, locale),
        total: formatMoney(selectedReceipt.total_amount, selectedReceipt.currency, locale),
        paid: formatMoney(selectedReceipt.paid_amount, selectedReceipt.currency, locale),
        balance: formatMoney(selectedReceipt.balance_due, selectedReceipt.currency, locale),
        footer: null,
        labels: {
          customer: t("Customer"),
          issued: t("Issued"),
          line: t("Line"),
          qty: t("Qty"),
          total: t("Total"),
          subtotal: t("Subtotal"),
          service: t("Service"),
          vat: t("VAT"),
          paid: t("Paid"),
          balance: t("Balance"),
        },
      });
      const printed = await printReceiptDocument(popup, documentHtml);
      if (!printed) {
        setError(t("Could not open the receipt print window. Allow popups and try again."));
        return;
      }
      try {
        const updatedReceipt = await providerApi.recordReceiptPrint(selectedReceipt.id, { copy_type: "customer" });
        await refreshReceiptLane(updatedReceipt, { syncWorkspace: false });
        setNotice(t("Print recorded for {number}.", { number: updatedReceipt.receipt_number }));
      } catch {
        setError(t("Receipt printed, but the print count could not be updated."));
      }
    } catch (printError) {
      closeReceiptPrintWindow(popup);
      setError(printError instanceof Error ? printError.message : t("Could not record print."));
    }
  };

  const voidSelectedReceipt = async () => {
    if (!selectedReceipt) {
      return;
    }

    setVoidingReceipt(true);
    setError(null);
    setNotice(null);
    try {
      if (voidReceiptInvalid) {
        throw new Error(t("Enter a reason before voiding this receipt."));
      }
      const updatedReceipt = await providerApi.voidReceipt(selectedReceipt.id, { reason: voidReason.trim() });
      await refreshReceiptLane(updatedReceipt);
      setNotice(t("Receipt {number} voided.", { number: updatedReceipt.receipt_number }));
    } catch (voidError) {
      setError(voidError instanceof Error ? voidError.message : t("Could not void receipt."));
    } finally {
      setVoidingReceipt(false);
    }
  };

  const canRefundSelectedReceipt = Boolean(selectedReceipt && selectedReceipt.status === "issued" && amountToCents(selectedReceipt.paid_amount) > 0);
  const refundAmountCents = amountToCents(refundAmount);
  const maxRefundCents = selectedReceipt ? amountToCents(selectedReceipt.paid_amount) : 0;
  const refundInvalid = !canRefundSelectedReceipt || refundAmountCents <= 0 || refundAmountCents > maxRefundCents || refundReason.trim().length < 3;

  const refundSelectedReceipt = async () => {
    if (!selectedReceipt) {
      return;
    }

    setRefunding(true);
    setError(null);
    setNotice(null);
    try {
      if (refundInvalid) {
        throw new Error(t("Enter a refund amount up to the paid total and a reason."));
      }
      const updatedReceipt = await providerApi.recordReceiptRefund(selectedReceipt.id, {
        amount: centsToAmount(refundAmountCents),
        method: refundMethod,
        reason: refundReason.trim(),
      });
      await refreshReceiptLane(updatedReceipt);
      setRefundDialogOpen(false);
      setRefundAmount("");
      setRefundReason("");
      setNotice(t("Refund recorded for {number}.", { number: updatedReceipt.receipt_number }));
    } catch (refundError) {
      setError(refundError instanceof Error ? refundError.message : t("Could not record refund."));
    } finally {
      setRefunding(false);
    }
  };

  if (!selectedVenue) {
    return <div className="ops-empty">{t("Select a venue to manage receipts.")}</div>;
  }

  // Group the to-pay list by what the desk has to do: collect money first,
  // then tables still playing, then what is already settled.
  const sessionGroups = (() => {
    const waiting: ProviderSession[] = [];
    const playing: ProviderSession[] = [];
    const settled: ProviderSession[] = [];
    for (const session of registerSessions) {
      const receipt = sessionReceiptsById.get(session.id);
      const unpaid = !receipt || (receipt.status === "issued" && receipt.balance_due > 0);
      if (session.status === "active") playing.push(session);
      else if (unpaid) waiting.push(session);
      else settled.push(session);
    }
    return { waiting, playing, settled };
  })();
  const toPayCount = sessionGroups.waiting.length;
  const allowedMethods = (sessionReceipt || selectedReceipt ? receiptPaymentMethods : preview?.allowed_payment_methods ?? receiptPaymentMethods) as PaymentMethod[];
  const balanceAmount = centsToAmount(chargeBalanceCents);
  // Quick cash amounts: exact, then the next round notes above it.
  const cashSuggestions = Array.from(new Set([
    balanceAmount,
    ...[50, 100, 200, 500].map((step) => Math.ceil(balanceAmount / step) * step),
  ])).filter((value) => value >= balanceAmount).slice(0, 4);

  const renderSessionRow = (session: ProviderSession) => {
    const linkedReceipt = sessionReceiptsById.get(session.id);
    const unpaid = !linkedReceipt || (linkedReceipt.status === "issued" && linkedReceipt.balance_due > 0);
    // Before a receipt exists the full bill (food, service, VAT) isn't known yet, so no
    // amount is shown; after, show what is still owed, or what was paid.
    const amount = linkedReceipt ? (unpaid ? linkedReceipt.balance_due : linkedReceipt.paid_amount) : null;
    return (
      <button
        aria-pressed={selectedSessionId === session.id}
        className="checkout-row"
        data-active={selectedSessionId === session.id ? "true" : undefined}
        key={session.id}
        onClick={() => { selectSession(session.id); revealBill(); }}
        type="button"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{session.customer_name ?? t("Walk-in")}</span>
          <span className="block truncate text-xs text-[var(--color-text-soft)]">{session.unit_name}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-0.5">
          {amount !== null ? <span className="text-sm font-bold tabular-nums">{formatMoney(amount, linkedReceipt?.currency ?? "EGP", locale)}</span> : null}
          {session.status === "active" ? (
            <span className="text-xs text-[var(--color-text-soft)]">{t("Playing now")}</span>
          ) : linkedReceipt && !unpaid ? (
            <span className="text-xs text-[var(--color-success)]">
              {linkedReceipt.status === "issued" ? t("Paid") : t(receiptStatusLabelKey[linkedReceipt.status] ?? linkedReceipt.status)}
            </span>
          ) : linkedReceipt ? (
            <span className="text-xs text-[var(--color-amber)]">{t("Part paid")}</span>
          ) : (
            <span className="text-xs text-[var(--color-amber)]">{t("Not paid yet")}</span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <h1 className="ops-page-title">{t("Checkout")}</h1>
          <p className="ops-copy text-sm">
            {activeTab === "register"
              ? toPayCount > 0
                ? t(toPayCount === 1 ? "{count} bill waiting to be paid." : "{count} bills waiting to be paid.", { count: toPayCount })
                : t("Nothing waiting to be paid.")
              : t("Every receipt issued at {venue}.", { venue: selectedVenue.name })}
          </p>
        </div>
        <div aria-label={t("Receipts tabs")} className="pn-segment" role="tablist">
          <button aria-selected={activeTab === "register"} onClick={() => setActiveTab("register")} role="tab" type="button" aria-pressed={activeTab === "register"}>{t("To pay")}</button>
          <button aria-selected={activeTab === "history"} onClick={() => setActiveTab("history")} role="tab" type="button" aria-pressed={activeTab === "history"}>{t("Receipts")}</button>
        </div>
      </div>

      {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
      {notice ? <div className="ops-banner" data-tone="success" role="status">{notice}</div> : null}
      {!isOnline ? <div className="ops-banner" data-tone="warning">{t("Receipt and payment actions need a live connection.")}</div> : null}

      <div className="checkout-layout">
        {activeTab === "register" ? (
          <aside className="checkout-list ops-surface">
            {registerSessions.length === 0 ? <div className="ops-empty">{t("No sessions ready for receipt")}</div> : null}
            {([
              ["waiting", t("Waiting to pay"), sessionGroups.waiting],
              ["playing", t("Playing now"), sessionGroups.playing],
              ["settled", t("Paid"), sessionGroups.settled],
            ] as const).map(([key, title, sessions]) => sessions.length === 0 ? null : (
              <section aria-label={title} className="grid gap-1.5" key={key}>
                <h2 className="checkout-group-title" data-group={key}>{title} <span>{sessions.length}</span></h2>
                {sessions.map(renderSessionRow)}
              </section>
            ))}
          </aside>
        ) : (
          <aside className="checkout-list ops-surface">
            <div className="grid gap-2">
              <label className="ops-field">
                <span className="sr-only">{t("Search receipts")}</span>
                <input className="ops-input" onChange={(event) => setReceiptSearch(event.target.value)} placeholder={t("Search number, customer, phone")} value={receiptSearch} />
              </label>
              <label className="ops-field">
                <span className="sr-only">{t("Receipt status")}</span>
                <select className="ops-select" onChange={(event) => setReceiptStatusFilter(event.target.value)} value={receiptStatusFilter}>
                  <option value="">{t("All statuses")}</option>
                  <option value="issued">{t("Issued")}</option>
                  <option value="voided">{t("Voided")}</option>
                  <option value="refunded">{t("Refunded")}</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="ops-field">
                  <span className="ops-label">{t("From")}</span>
                  <input className="ops-input" onChange={(event) => setDateFrom(event.target.value)} type="date" value={dateFrom} />
                </label>
                <label className="ops-field">
                  <span className="ops-label">{t("To")}</span>
                  <input className="ops-input" onChange={(event) => setDateTo(event.target.value)} type="date" value={dateTo} />
                </label>
              </div>
            </div>
            {receipts.length === 0 ? <div className="ops-empty">{t("No receipts found.")}</div> : null}
            <div className="grid gap-1.5">
              {receipts.map((receipt) => (
                <button aria-pressed={selectedReceipt?.id === receipt.id} className="checkout-row" data-active={selectedReceipt?.id === receipt.id ? "true" : undefined} key={receipt.id} onClick={() => { selectReceipt(receipt); revealBill(); }} type="button">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{receipt.receipt_number}</span>
                    <span className="block truncate text-xs text-[var(--color-text-soft)]">{receipt.customer_name ?? t("Walk-in")}</span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-0.5">
                    <span className="text-sm font-bold tabular-nums">{formatMoney(receipt.total_amount, receipt.currency, locale)}</span>
                    <span className="text-xs text-[var(--color-text-soft)]">{t(receiptStatusLabelKey[receipt.status] ?? receipt.status)}</span>
                  </span>
                </button>
              ))}
            </div>
          </aside>
        )}

        <section className="checkout-bill ops-surface receipt-print-area" ref={billRef}>
          {loading ? <div className="ops-empty">{t("Building receipt preview...")}</div> : null}
          {!loading && !preview && !selectedReceipt ? <div className="ops-empty">{t("Select a session to see what is due.")}</div> : null}

          {(preview || selectedReceipt) && !loading ? (
            <>
              <header className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-[var(--color-text)]">
                    {selectedReceipt?.customer_name ?? selectedSession?.customer_name ?? t("Walk-in")}
                  </h2>
                  <p className="ops-copy text-sm">
                    {[
                      selectedSession?.unit_name,
                      selectedSession?.status === "active" ? t("Playing now") : null,
                      selectedReceipt ? t("Receipt {number}", { number: selectedReceipt.receipt_number }) : t("Not billed yet"),
                    ].filter(Boolean).join(" · ")}
                  </p>
                </div>
                {selectedReceipt ? (
                  <button className="ops-button" data-size="sm" data-variant="secondary" disabled={!isOnline} onClick={() => void printSelectedReceipt()} type="button">
                    {selectedReceipt.print_count > 0 ? t("Reprint") : t("Print")}
                  </button>
                ) : null}
              </header>

              {selectedSession?.status === "active" ? (
                <p className="ops-banner" data-tone="info">{t("This table is still playing. The bill grows until you end the session on the Floor; you can take a payment now if the guest wants to pay early.")}</p>
              ) : null}
              {selectedReceipt?.status === "voided" ? <div className="ops-banner" data-tone="warning">{t("Voided: {reason}", { reason: selectedReceipt.void_reason ?? t("No reason recorded") })}</div> : null}

              <table className="checkout-lines">
                <thead>
                  <tr>
                    <th>{t("Item")}</th>
                    <th className="text-end">{t("Qty")}</th>
                    <th className="text-end">{t("Total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLines.map((line) => {
                    const lineKey = "id" in line ? String(line.id) : `${line.sort_order}-${line.description}`;
                    return (
                      <tr key={lineKey}>
                        <td>{line.description}</td>
                        <td className="text-end tabular-nums">{line.quantity}</td>
                        <td className="text-end tabular-nums">{formatMoney(line.total, activeCurrency, locale)}</td>
                      </tr>
                    );
                  })}
                  {activeLines.length === 0 ? (
                    <tr><td colSpan={3} className="ops-copy">{t("No billable lines yet.")}</td></tr>
                  ) : null}
                </tbody>
              </table>

              <dl className="checkout-totals">
                <div><dt>{t("Subtotal")}</dt><dd>{formatMoney(activeSubtotal, activeCurrency, locale)}</dd></div>
                {activeServiceFee > 0 ? <div><dt>{t("Service")}</dt><dd>{formatMoney(activeServiceFee, activeCurrency, locale)}</dd></div> : null}
                {activeTax > 0 ? <div><dt>{t("VAT")}</dt><dd>{formatMoney(activeTax, activeCurrency, locale)}</dd></div> : null}
                <div data-total="true"><dt>{t("Total")}</dt><dd>{formatMoney(activeTotal, activeCurrency, locale)}</dd></div>
                {activePaid > 0 ? <div><dt>{t("Paid")}</dt><dd>{formatMoney(activePaid, activeCurrency, locale)}</dd></div> : null}
              </dl>

              {chargeBalanceCents > 0 ? (
                <div className="checkout-pay">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="ops-label">{t("To pay")}</p>
                    <p className="checkout-due">{formatMoney(balanceAmount, chargeCurrency, locale)}</p>
                  </div>
                  <div aria-label={t("Payment method")} className="checkout-methods" role="group">
                    {allowedMethods.map((method) => (
                      <button
                        aria-pressed={paymentMethod === method}
                        key={method}
                        onClick={() => {
                          setPaymentMethod(method);
                          if (method !== "cash") setPaidAmount(balanceAmount.toFixed(2));
                        }}
                        type="button"
                      >
                        {methodLabel(method, t)}
                      </button>
                    ))}
                  </div>
                  {paymentMethod === "cash" ? (
                    <div className="grid gap-2">
                      <label className="ops-field">
                        <span className="ops-label">{t("Cash received")}</span>
                        <input className="ops-input checkout-amount" inputMode="decimal" min="0" onChange={(event) => setPaidAmount(event.target.value)} step="0.01" type="number" value={paidAmount} />
                      </label>
                      <div aria-label={t("Quick amounts")} className="flex flex-wrap gap-1.5" role="group">
                        {cashSuggestions.map((value) => (
                          <button className="ops-button" data-size="sm" data-variant="secondary" key={value} onClick={() => setPaidAmount(value.toFixed(2))} type="button">
                            {value === balanceAmount ? t("Exact") : formatMoney(value, chargeCurrency, locale)}
                          </button>
                        ))}
                      </div>
                      {chargeChangeDue > 0 ? (
                        <p className="checkout-change">{t("Give back {amount} change", { amount: formatMoney(chargeChangeDue, chargeCurrency, locale) })}</p>
                      ) : null}
                      <NoShiftCashNotice isOnline={isOnline} venue={selectedVenue} />
                    </div>
                  ) : paymentMethod ? (
                    <label className="ops-field">
                      <span className="ops-label">{t("Amount")}</span>
                      <input className="ops-input checkout-amount" inputMode="decimal" min="0" onChange={(event) => setPaidAmount(event.target.value)} step="0.01" type="number" value={paidAmount} />
                    </label>
                  ) : null}
                  {chargeInvalid && paidAmount ? <p className="text-xs font-semibold text-[var(--color-error)]">{t("Enter a received amount within the receipt balance.")}</p> : null}
                  <button className="ops-button checkout-take" data-variant="primary" disabled={!isOnline || charging || chargeInvalid} onClick={() => void recordCharge()} type="button">
                    {charging
                      ? t("Recording...")
                      : chargeAppliedCents > 0 && paymentMethod
                        ? t("Take {amount} by {method}", { amount: formatMoney(chargeAppliedAmount, chargeCurrency, locale), method: methodLabel(paymentMethod, t).toLowerCase() })
                        : t("Record payment")}
                  </button>
                  {preview && !sessionReceipt && preview.can_issue_unpaid ? (
                    <button className="ops-button" data-size="sm" data-variant="secondary" disabled={!isOnline || charging} onClick={() => void issueUnpaidReceipt()} type="button">
                      {t("Issue without payment")}
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="grid gap-2">
                  <div className="ops-banner" data-tone="success">{sessionReceipt ? t("Paid in full") : t("No balance due")}</div>
                  {canIssuePaidReceipt ? (
                    <button className="ops-button" data-variant="primary" disabled={!isOnline || charging} onClick={() => void issuePaidReceipt()} type="button">
                      {charging ? t("Issuing...") : t("Issue receipt")}
                    </button>
                  ) : null}
                </div>
              )}

              {selectedReceipt ? (
                <details className="booking-disclosure">
                  <summary>{t("Receipt details")}</summary>
                  <dl className="checkout-totals">
                    <div><dt>{t("Issued")}</dt><dd>{formatDateTime(selectedReceipt.issued_at, locale)}</dd></div>
                    <div><dt>{t("Status")}</dt><dd>{t(receiptStatusLabelKey[selectedReceipt.status] ?? selectedReceipt.status)}</dd></div>
                    <div><dt>{t("Prints")}</dt><dd>{selectedReceipt.print_count}</dd></div>
                  </dl>

                  {selectedReceipt.payments.length > 0 ? (
                    <div className="grid gap-1">
                      <p className="ops-label">{t("Payments")}</p>
                      {selectedReceipt.payments.slice(-4).reverse().map((payment) => {
                        const paymentChange = paymentChangeAmount(payment);
                        const paymentTendered = paymentTenderedAmount(payment);
                        return (
                          <div className="checkout-row" key={payment.id}>
                            <span className="min-w-0 truncate text-sm">
                              {methodLabel(payment.method, t)} · {formatDateTime(payment.collected_at, locale)}
                              {paymentTendered !== null ? ` · ${t("Received")}: ${formatMoney(paymentTendered, selectedReceipt.currency, locale)}` : ""}
                              {paymentChange !== null && paymentChange > 0 ? ` · ${t("Change due")}: ${formatMoney(paymentChange, selectedReceipt.currency, locale)}` : ""}
                            </span>
                            <span className="shrink-0 text-sm font-bold tabular-nums">{formatMoney(payment.amount, selectedReceipt.currency, locale)}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {selectedReceipt.events.length > 0 ? (
                    <div className="grid gap-1">
                      <p className="ops-label">{t("Events")}</p>
                      {selectedReceipt.events.slice(0, 4).map((event) => (
                        <div className="flex justify-between gap-3 text-xs text-[var(--color-text-soft)]" key={event.id}>
                          <span>{t(event.event_type)}</span>
                          <span>{formatDateTime(event.created_at, locale)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {selectedReceipt.status === "issued" ? (
                    !selectedReceiptHasPayments ? (
                      <div className="grid gap-2">
                        <p className="ops-label">{t("Void receipt")}</p>
                        <p className="ops-copy text-xs">{t("Only for receipts issued by mistake, before any payment.")}</p>
                        <div className="flex flex-wrap items-end gap-2">
                          <label className="ops-field min-w-0 flex-1">
                            <span className="ops-label">{t("Reason")}</span>
                            <input className="ops-input" maxLength={500} onChange={(event) => setVoidReason(event.target.value)} placeholder={t("Mistake, duplicate, customer left...")} value={voidReason} />
                          </label>
                          <button className="ops-button" data-size="sm" data-variant="secondary" disabled={!isOnline || voidingReceipt || voidReceiptInvalid} onClick={() => void voidSelectedReceipt()} type="button">
                            {voidingReceipt ? t("Voiding...") : t("Void receipt")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        <p className="ops-label">{t("Refund receipt")}</p>
                        <p className="ops-copy text-xs">{t("Issue a refund to return all or part of the paid amount.")}</p>
                        <button
                          className="ops-button justify-self-start"
                          data-size="sm"
                          data-variant="warning"
                          disabled={!isOnline}
                          onClick={() => {
                            setRefundMethod("cash");
                            setRefundAmount(selectedReceipt.paid_amount.toFixed(2));
                            setRefundReason("");
                            setRefundDialogOpen(true);
                          }}
                          type="button"
                        >
                          {t("Refund")}
                        </button>
                      </div>
                    )
                  ) : null}
                </details>
              ) : null}
            </>
          ) : null}
        </section>
      </div>

      {refundDialogOpen && selectedReceipt ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
          <div className="ops-surface w-full max-w-md grid gap-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="ops-title">{t("Refund {number}", { number: selectedReceipt.receipt_number })}</p>
                <p className="ops-muted">{t("Paid")}: {formatMoney(selectedReceipt.paid_amount, selectedReceipt.currency, locale)}</p>
              </div>
              <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setRefundDialogOpen(false)} type="button">{t("Close")}</button>
            </div>
            <label className="ops-field">
              <span className="ops-label">{t("Method")}</span>
              <select className="ops-select" onChange={(event) => setRefundMethod(event.target.value as PaymentMethod)} value={refundMethod}>
                {receiptPaymentMethods.map((method) => <option key={method} value={method}>{t(method)}</option>)}
              </select>
            </label>
            <label className="ops-field">
              <span className="ops-label">{t("Amount")}</span>
              <input className="ops-input" min="0.01" max={selectedReceipt.paid_amount} onChange={(event) => setRefundAmount(event.target.value)} step="0.01" type="number" value={refundAmount} />
            </label>
            <label className="ops-field">
              <span className="ops-label">{t("Reason")}</span>
              <input className="ops-input" maxLength={500} onChange={(event) => setRefundReason(event.target.value)} placeholder={t("Customer request, overcharge...")} value={refundReason} />
            </label>
            <div className="flex justify-end gap-2">
              <button className="ops-button" data-variant="secondary" onClick={() => setRefundDialogOpen(false)} type="button">{t("Cancel")}</button>
              <button className="ops-button" data-variant="danger" disabled={!isOnline || refunding || refundInvalid} onClick={() => void refundSelectedReceipt()} type="button">
                {refunding ? t("Refunding...") : t("Record refund")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
